import { useEffect, useState } from "react"
import { api, ApiError, type Job, type Finding } from "./api"
import { PipelineStepper } from "./PipelineStepper"
import { RiskBreakdown } from "./RiskBreakdown"

const STATUS_CLASS: Record<string, string> = {
    PROCESSING: "status-pending",
    APPROVED: "status-good",
    COMPLETED: "status-good",
    PATCHED: "status-warn",
    MANUAL_REVIEW: "status-warn",
    REJECTED: "status-bad",
    FAILED: "status-bad",
}

function StatusBadge({ status }: { status: string }) {
    return <span className={`badge ${STATUS_CLASS[status] ?? ""}`}>{status}</span>
}

function JobDetail({ jobId }: { jobId: string }) {
    const [job, setJob] = useState<Job | null>(null)
    const [findings, setFindings] = useState<Finding[]>([])
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        let timer: ReturnType<typeof setInterval>

        async function load() {
            try {
                const [j, f] = await Promise.all([api.getJob(jobId), api.getFindings(jobId)])
                if (cancelled) return
                setJob(j)
                setFindings(f)
                setError(null)
                if (j.status !== "PROCESSING" && timer) clearInterval(timer)
            } catch (err) {
                if (!cancelled) setError(err instanceof ApiError ? err.message : "Failed to load job")
            }
        }

        load()
        timer = setInterval(load, 3000)
        return () => {
            cancelled = true
            clearInterval(timer)
        }
    }, [jobId])

    if (error) return <p className="error">{error}</p>
    if (!job) return <p>Loading…</p>

    return (
        <div className="job-detail">
            <h3>Job {job.id}</h3>
            <dl>
                <dt>Status</dt>
                <dd>
                    <StatusBadge status={job.status} />
                </dd>
                <dt>Risk score</dt>
                <dd>{job.riskScore}</dd>
                <dt>Patched</dt>
                <dd>{job.patched ? "Yes" : "No"}</dd>
                <dt>Source</dt>
                <dd className="mono">{job.videoUrl}</dd>
                <dt>Created</dt>
                <dd>{new Date(job.createdAt).toLocaleString()}</dd>
            </dl>

            <h4>Pipeline progress</h4>
            <PipelineStepper job={job} />

            {job.riskBreakdown && <RiskBreakdown breakdown={job.riskBreakdown} score={job.riskScore} />}

            {job.safeVideoUrl && (
                <>
                    <h4>Published video</h4>
                    {/* Served through our own API rather than safeVideoUrl
                        directly — the bucket can't be public (org policy),
                        so the raw GCS URL 403s in a browser. */}
                    <video controls src={`/jobs/${job.id}/video`} style={{ maxWidth: "100%" }} />
                </>
            )}

            <h4>Findings ({findings.length})</h4>
            {findings.length === 0 ? (
                <p className="muted">No violations detected.</p>
            ) : (
                <table>
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Confidence</th>
                            <th>Timestamp</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {findings.map((f) => (
                            <tr key={f.id}>
                                <td>{f.type}</td>
                                <td>{(f.confidence * 100).toFixed(0)}%</td>
                                <td className="mono">{f.timestamp}</td>
                                <td>{f.action}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    )
}

export function JobsView() {
    const [jobs, setJobs] = useState<Job[]>([])
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [videoUrl, setVideoUrl] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    async function loadJobs() {
        try {
            setJobs(await api.listJobs())
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to load jobs")
        }
    }

    useEffect(() => {
        loadJobs()
        const timer = setInterval(loadJobs, 5000)
        return () => clearInterval(timer)
    }, [])

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        setSubmitting(true)
        setError(null)
        try {
            const { id } = await api.createJob(videoUrl)
            setVideoUrl("")
            await loadJobs()
            setSelectedId(id)
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to submit job")
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="jobs-view">
            <section className="submit-panel">
                <h2>Submit a video</h2>
                <form onSubmit={handleSubmit}>
                    <input
                        type="url"
                        placeholder="https://example.com/video.mp4"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        required
                    />
                    <button type="submit" disabled={submitting}>
                        {submitting ? "Submitting…" : "Submit"}
                    </button>
                </form>
                {error && <p className="error">{error}</p>}
            </section>

            <div className="jobs-layout">
                <section className="job-list">
                    <h2>Recent jobs</h2>
                    {jobs.length === 0 ? (
                        <p className="muted">No jobs yet.</p>
                    ) : (
                        <ul>
                            {jobs.map((job) => (
                                <li
                                    key={job.id}
                                    className={job.id === selectedId ? "selected" : ""}
                                    onClick={() => setSelectedId(job.id)}
                                >
                                    <StatusBadge status={job.status} />
                                    <span className="mono job-id">{job.id.slice(-8)}</span>
                                    <span className="job-score">risk {job.riskScore}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="job-detail-panel">
                    {selectedId ? <JobDetail jobId={selectedId} /> : <p className="muted">Select a job to view details.</p>}
                </section>
            </div>
        </div>
    )
}
